/**
 * TODO:
 * Email verification
 */
class ProductRegisterForm {
  constructor() {
    this.initFormElements();
    this.initSkuSearchElements();
    this.initEventHandlers(this);
  }

  /**
   * ### Initialize form elements
   */
  initFormElements() {
    this.form = document.querySelector("#mil_email_signup");
    this.purchasedTypeInput = document.querySelector("#product-purchased-type");
    this.isGiftInput = this.form.querySelector("#is-gift-input");
    this.skuInput = this.form.querySelector("#sku");
    this.emailInput = this.form.querySelector("#email");
    this.verifyEmailInput = this.form.querySelector("#verify-email");
    this.verifyEmailError = this.form.querySelector("#email-verify-error");
    this.submitBtn = this.form.querySelector(".klaviyo_submit_button");
    this.formOverlay = document.querySelector("#form-sku-search-overlay");
    this.successWindow = document.querySelector("#success-window");
  }

  /**
   * ### Initialize SKU search elements
   */
  initSkuSearchElements() {
    this.skuSearchResultSection = document.querySelector(
      "#product-register-sku-search-results"
    );
    this.skuResults = this.skuSearchResultSection.querySelector("#sku-results");
    this.loadingOverlay =
      this.skuSearchResultSection.querySelector(".loading-overlay");
    this.skuErrorMessage = this.skuSearchResultSection.querySelector(
      "#sku-results-message"
    );
    this.skuErrors = {
      skuNotFound:
        "Code cannot be found. Please try again and make sure to enter the code with dashes.",
      globalErrors:
        "Something went wrong. Please email us at support@originalgrain.com if this error persist.",
    };
  }

  /**
   * ### Event handlers
   */
  initEventHandlers() {
    // Purchase type inputs
    this.purchasedTypeInput.addEventListener(
      "click",
      this.handlePurchaseTypeClick.bind(this)
    );

    // SKU input
    this.skuInput.addEventListener("input", this.handleSkuInput.bind(this));
    this.skuInput.addEventListener("focusin", () => {
      this.showSkuSearchResults(true);
      this.showFormOverlay(true);
    });

    // SKU search results
    this.skuSearchResultSection.addEventListener(
      "click",
      this.handleSearchResultSection.bind(this)
    );

    // Form overlay
    this.formOverlay.addEventListener("click", () => {
      this.showSkuSearchResults(false);
      this.showFormOverlay(false);
    });

    // Verify email input
    this.verifyEmailInput.addEventListener(
      "focusout",
      this.verifyEmail.bind(this)
    );
    this.verifyEmailInput.addEventListener(
      "change",
      this.verifyEmail.bind(this)
    );
  }

  /**
   * ### Handles SKU input changes and triggers SKU search.
   * @param {Event} event The input change event
   */
  handleSkuInput(event) {
    const { value } = event.target;
    if (value.length > 0) {
      this.searchSkuWatches(value);
    } else {
      this.skuResults.innerHTML = "";
      this.showSkuSearchResults(false);
    }
  }

  /**
   * ### Event handler for the purchase type radio button click event
   * @param {Event} event The click event
   */
  handlePurchaseTypeClick(event) {
    const singlePurchase = event.target.closest("#single-purchase");
    const giftPurchase = event.target.closest("#gift-purchase");

    if (singlePurchase) {
      this.isGiftInput.value = false;
      return;
    }
    if (giftPurchase) {
      this.isGiftInput.value = true;
      return;
    }
  }

  /**
   * ### Event handler for the search results section
   * @param {Event} event The click event
   */
  handleSearchResultSection(event) {
    // Get element
    const searchElement = event.target.closest(".sku-search-product-result");
    if (!searchElement) return;
    const { productSku } = searchElement.dataset;
    productSku && this.populateSkuInput(productSku);
    this.showFormOverlay(false);
    this.showSkuSearchResults(false);
  }

  /**
   * ### Asynchronously searches for SKU watches and updates the display
   * @param {String} sku The SKU value to search for.
   */
  async searchSkuWatches(sku) {
    const controller = new AbortController();
    this.setSkuErrorMessage();
    this.setLoadingState(true);
    this.showFormOverlay(true);
    this.skuResults.innerHTML = "";

    try {
      /**
       * Search request
       * Parameters:
       * · q=sku → query search with sku value
       * · resources[type]=product → get only product results
       * · section_id=product-register-sku-search-template → provide a HTML template to render results of the search
       * · resources[options][fields]=variants.sku → include search sku as a search option
       * · resources[options][unavailable_products]=show → show not available products
       *   ↪️ ☝🏻 The showed product doesn't retrieve the image or SKU. The input will stay with the searched value
       * · For more info: https://shopify.dev/docs/api/ajax/reference/predictive-search
       */
      const request = await fetch(
        `${routes.predictive_search_url}?q=${sku}&resources[type]=product&section_id=product-register-sku-search-template&resources[options][fields]=variants.sku&resources[options][unavailable_products]=show`,
        {
          signal: controller.signal,
        }
      );

      // Show error message
      if (!request.ok) {
        this.setSkuErrorMessage(this.skuErrors.globalErrors);
        return;
      }

      // Get response text
      const data = await request.text();

      // Parse response markup
      const resultMarkup = new DOMParser()
        .parseFromString(data, "text/html")
        .querySelector("#product-register-sku-search-template");

      // Check if search has results
      const searchHasResults = this.checkSkuResults(resultMarkup);
      if (!searchHasResults) {
        this.setSkuErrorMessage(this.skuErrors.skuNotFound);
        return;
      }

      // Reset sku message
      this.setSkuErrorMessage();

      // Insert markup
      this.skuResults.innerHTML = resultMarkup.innerHTML;

      // Show Section
      this.showSkuSearchResults(true);
    } catch (error) {
      console.error(
        `❌ 👉🏻 ProductRegisterForm | searchSkuWatches | ${error.message}`
      );
    } finally {
      this.setLoadingState(false);
      controller.abort();
    }
  }

  /**
   * ### Checks for SKU search results in the provided markup.
   * @param {HTMLElement} markup The HTML markup to be examined for SKU search results.
   * @returns {boolean} `true` if SKU search results are found, otherwise `false`.
   */
  checkSkuResults(markup) {
    const skuResultsElement = markup.querySelectorAll(
      ".sku-search-product-result"
    );
    return skuResultsElement.length > 0;
  }

  /**
   * ### Set the loading state for the SKU search.
   * @param {boolean} isLoading Indicates whether the loading state should be set or removed.
   */
  setLoadingState(isLoading) {
    this.loadingOverlay.classList[isLoading ? "remove" : "add"]("hidden");
  }

  /**
   * ### Sets a custom SKU error message
   * @param {String} message Custom message, could be empty
   */
  setSkuErrorMessage(message) {
    this.skuErrorMessage.innerHTML = message || "";
  }

  /**
   * ### Controls the visibility of SKU search results section.
   * @param {Boolean} isVisible  Flag to make section visible (true) or hidden (false).
   * @returns
   */
  showSkuSearchResults(isVisible) {
    const hasContent = this.checkSkuResults(this.skuResults);
    if (!hasContent) return;
    this.skuSearchResultSection.dataset.showSkuSearchResults = isVisible;
  }

  /**
   * ### Controls the overlay visibility
   *
   * The purpose to this element is to close the search results.
   * Falls down to avoid listening the `document.body` events
   * @param {Boolean} isVisible Flag to make overlay clickable
   */
  showFormOverlay(isVisible) {
    this.formOverlay.dataset.showFormSkuSearchOverlay = isVisible;
  }

  /**
   * ### Populate sku input
   * @param {String} value Value to set in the input
   */
  populateSkuInput(value) {
    this.skuInput.value = value;
  }

  /**
   * ### Verify email function
   * @param {Event} event Focus out event
   */
  verifyEmail(event) {
    const { value } = event.target;
    const trimmedInputVal = value.trim();
    const trimmedEmail = this.emailInput.value.trim();

    if (trimmedEmail !== trimmedInputVal) {
      this.verifyEmailError.dataset.showEmailVerifyError = true;
      this.submitBtn.disabled = true;
    } else {
      this.verifyEmailError.dataset.showEmailVerifyError = false;
      this.submitBtn.disabled = false;
    }
  }

  showSuccessSubmit() {
    this.form.classList.add("success-submit");
    this.successWindow.dataset.showSuccessWindow = true;
  }
}

/**
 * # Popup Image.
 * Use to show a guiding image to the user
 * for getting the product SKU
 */
class PopupImage {
  constructor() {
    // Elements
    this.body = document.body;
    this.container = document.querySelector("#popup-example-image");
    this.overlay = document.querySelector("#popup-example-overlay");
    this.trigger = document.querySelector("#popup-example-trigger");

    // Event listeners for the trigger and overlay
    this.trigger.addEventListener("click", this.open.bind(this));
    this.overlay.addEventListener("click", this.close.bind(this));
  }

  /**
   * ### Open the image popup
   */
  open() {
    this.body.classList.add("overflow-hidden");
    this.container.dataset.showExample = true;
  }

  /**
   * ### Close the image popup
   */
  close() {
    this.body.classList.remove("overflow-hidden");
    this.container.dataset.showExample = false;
  }
}

// ProductRegisterForm instance
const productRegisterForm = new ProductRegisterForm();

// PopupImage instance
const popupImage = new PopupImage();

document.addEventListener("DOMContentLoaded", function () {
  // Klaviyo instance
  KlaviyoSubscribe.attachToForms("#mil_email_signup", {
    hide_form_on_success: true,
    success_message: "Thanks for register your watch!",
    success: function () {
      productRegisterForm.showSuccessSubmit();
    },
  });
});
